import { RefObject, useEffect, useState } from 'react';

import debounce from 'lodash.debounce';

import isContentOverflowingElement from '../isContentOverflowingElement';

export type UseIsContentOverflowingOptions = {
  waitTime?: number;
  toleranceY?: number;
};

/**
 * Watches the element for changes in the overflowing state
 * @param elementRef A ref to the element being observed
 * @returns a boolean to tell if the element is overflowing or not
 */
function useIsContentOverflowing<Element extends HTMLElement>(
  elementRef: RefObject<Element | null> | null | undefined,
  options: UseIsContentOverflowingOptions = {},
): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const { waitTime, toleranceY } = options;

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let debouncedCheckIsOverflowing: ReturnType<
      typeof debounce<() => void>
    > | null = null;

    if (elementRef?.current) {
      const checkIsOverflowing = () =>
        setIsOverflowing(
          isContentOverflowingElement(
            elementRef,
            toleranceY ? { toleranceY } : undefined,
          ),
        );

      debouncedCheckIsOverflowing = debounce(
        checkIsOverflowing,
        waitTime ?? 100,
      );

      // Check if overflowing on initial mount
      checkIsOverflowing();

      // Check if overflowing when target's dimension changes
      resizeObserver = new ResizeObserver(debouncedCheckIsOverflowing);

      resizeObserver.observe(elementRef.current);

      // Check if overflowing when target's text content changes
      mutationObserver = new MutationObserver(debouncedCheckIsOverflowing);
      mutationObserver.observe(elementRef.current, {
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      debouncedCheckIsOverflowing?.cancel();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- elementRef.current triggers re-run when ref attaches late
  }, [elementRef?.current, waitTime, toleranceY]);

  return isOverflowing;
}

export default useIsContentOverflowing;
